import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../config/api';
import Footer from '../components/Footer';
import { AlertCircle, Briefcase, ArrowRight, Search, Sparkles } from 'lucide-react';

const InternshipCard = ({ internship }) => (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col hover-glow hover:border-indigo-300 shadow-sm transition-all duration-300">
        <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900 line-clamp-2 leading-snug">
                {internship.title}
            </h3>
            {internship.tag && (
                <span className="px-2.5 py-1 rounded bg-indigo-50 text-indigo-700 text-[9px] font-extrabold uppercase tracking-wider border border-indigo-100 shrink-0 ml-3">
                    {internship.tag}
                </span>
            )}
        </div>
        <p className="text-sm text-slate-500 mb-6 flex-1 line-clamp-3 leading-relaxed">
            {internship.shortDescription || internship.description}
        </p>

        <div className="grid grid-cols-2 gap-4 mb-6 bg-slate-50 p-4 rounded-xl border border-slate-100">
            <div>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Duration</p>
                <p className="text-sm font-semibold text-slate-800">{internship.duration}</p>
            </div>
            <div>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Stipend</p>
                <p className="text-sm font-bold text-indigo-600">₹{internship.stipend}</p>
            </div>
        </div>

        {Array.isArray(internship.technologies) && (
            <div className="flex flex-wrap gap-1.5 mb-6">
                {internship.technologies.slice(0, 4).map((tech) => (
                    <span
                        key={tech}
                        className="bg-slate-50 text-slate-600 text-xs font-semibold px-2.5 py-1 rounded-lg border border-slate-100"
                    >
                        {tech}
                    </span>
                ))}
            </div>
        )}
        <div className="pt-4 border-t border-slate-100 mt-auto">
            <Link
                to={`/internships/${internship._id}`}
                className="flex items-center text-sm font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
            >
                View Full Details <ArrowRight className="ml-1.5 h-4 w-4" />
            </Link>
        </div>
    </div>
);

const InternshipSkeleton = () => (
    <div className="bg-slate-50 border border-slate-200/60 rounded-2xl p-6 space-y-4 animate-pulse">
        <div className="h-6 bg-slate-200 rounded-md w-3/4"></div>
        <div className="h-4 bg-slate-200 rounded-md w-full"></div>
        <div className="h-4 bg-slate-200 rounded-md w-5/6"></div>
        <div className="grid grid-cols-2 gap-4">
            <div className="h-10 bg-slate-200 rounded-xl"></div>
            <div className="h-10 bg-slate-200 rounded-xl"></div>
        </div>
        <div className="h-8 bg-slate-200 rounded-md w-1/3"></div>
    </div>
);

const Internships = () => {
    const [internships, setInternships] = useState([]);
    const [filteredInternships, setFilteredInternships] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('All');

    useEffect(() => {
        const fetchInternships = async () => {
            try {
                const { data } = await api.get('/internships/public');
                setInternships(data.data);
                setFilteredInternships(data.data);
            } catch (err) {
                setError('Failed to load internships.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchInternships();
    }, []);

    // Filter and search logic combined
    useEffect(() => {
        let result = internships;

        // Apply Tab Filter
        if (activeTab !== 'All') {
            result = result.filter(item => {
                const titleMatch = item.title.toLowerCase().includes(activeTab.toLowerCase());
                const tagMatch = item.tag && item.tag.toLowerCase().includes(activeTab.toLowerCase());
                const techMatch = item.technologies && item.technologies.some(tech => tech.toLowerCase().includes(activeTab.toLowerCase()));
                return titleMatch || tagMatch || techMatch;
            });
        }

        // Apply Search query
        if (searchQuery.trim() !== '') {
            const query = searchQuery.toLowerCase();
            result = result.filter(item => 
                item.title.toLowerCase().includes(query) || 
                (item.shortDescription && item.shortDescription.toLowerCase().includes(query)) ||
                (item.tag && item.tag.toLowerCase().includes(query)) ||
                (item.technologies && item.technologies.some(tech => tech.toLowerCase().includes(query)))
            );
        }

        setFilteredInternships(result);
    }, [searchQuery, activeTab, internships]);

    const categories = ['All', 'Web', 'Python', 'Data', 'Design'];

    return (
        <div className="min-h-screen bg-slate-50 font-sans flex flex-col text-slate-800">
            <div className="flex-1 py-16 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 font-bold text-xs border border-indigo-200 mb-4 uppercase tracking-wider shadow-sm">
                            <Sparkles className="w-3.5 h-3.5 text-indigo-600" /> Enroll Today
                        </span>
                        <h1 className="text-4xl sm:text-5xl font-extrabold mb-6 text-slate-900 tracking-tight">
                            Available Internships
                        </h1>
                        <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
                            Discover structured virtual internship programs to kickstart your career journey with verifiable hands-on project execution.
                        </p>
                    </div>

                    {/* Controls (Search & Category pills) */}
                    <div className="mb-12 flex flex-col md:flex-row items-center justify-between gap-6 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                        {/* Search Input */}
                        <div className="relative w-full md:w-80">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                            <input 
                                type="text"
                                placeholder="Search internships or skills..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-slate-50 text-slate-800 transition-all"
                            />
                        </div>
                        {/* Category Pills */}
                        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto overflow-x-auto pb-1 md:pb-0">
                            {categories.map((cat) => (
                                <button
                                    key={cat}
                                    onClick={() => setActiveTab(cat)}
                                    className={`px-4 py-2 rounded-xl text-xs font-bold tracking-wide transition-all border ${
                                        activeTab === cat 
                                        ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm shadow-indigo-100' 
                                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                    }`}
                                >
                                    {cat === 'All' ? 'All Roles' : cat}
                                </button>
                            ))}
                        </div>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                            {[...Array(3)].map((_, i) => <InternshipSkeleton key={i} />)}
                        </div>
                    ) : error ? (
                        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-8 text-center max-w-2xl mx-auto">
                            <AlertCircle className="h-10 w-10 text-rose-500 mx-auto mb-4" />
                            <h2 className="text-lg font-bold text-rose-700">{error}</h2>
                        </div>
                    ) : filteredInternships.length === 0 ? (
                        <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center max-w-2xl mx-auto shadow-sm">
                            <Briefcase className="h-14 w-14 text-slate-300 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-slate-900 mb-1">No Programs Found</h3>
                            <p className="text-slate-500 text-sm">We couldn't find any internships matching your search criteria.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                            {filteredInternships.map(internship => (
                                <InternshipCard key={internship._id} internship={internship} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default Internships;